import React, { useMemo, useState } from 'react';
import EconomicIndicatorCard from './EconomicIndicatorCard';
import RangeControl, { ECON_PRESETS } from './RangeControl';

const defaultEconRange = () => {
  const end = new Date();
  const start = new Date();
  start.setFullYear(end.getFullYear() - 5);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

function EconomicIndicatorsSection({ indicators, errors }) {
  const [dateRange, setDateRange] = useState(defaultEconRange);
  const [sortMode, setSortMode] = useState('default'); // 'default' | 'next_release'

  // Use Headline CPI history as the brush reference shape (longest monthly
  // econ series in the catalog). Falls back to the first available indicator
  // if CPI fetch failed.
  const referenceSeries = useMemo(() => {
    const cpi = (indicators || []).find((i) => i.key === 'headline_cpi_yoy');
    if (cpi?.history?.length) return cpi.history;
    const firstWithHistory = (indicators || []).find((i) => i.history?.length);
    return firstWithHistory?.history || [];
  }, [indicators]);

  // 'default' preserves the backend's importance order. 'next_release' sorts a
  // copy ascending by next_release_date (soonest first); indicators with no
  // scheduled date fall to the end. ISO YYYY-MM-DD strings sort lexically.
  const sortedIndicators = useMemo(() => {
    const list = indicators || [];
    if (sortMode !== 'next_release') return list;
    return [...list].sort((a, b) => {
      const da = a.next_release_date || '';
      const db = b.next_release_date || '';
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });
  }, [indicators, sortMode]);

  const sortButtonClass = (active) =>
    `px-3 py-1.5 text-xs rounded-md border ${
      active
        ? 'border-blue-500/60 text-blue-300'
        : 'border-gray-600 text-gray-300 hover:bg-gray-700/40'
    }`;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-white">Economic Indicators</h3>
          <p className="text-xs text-gray-500">FRED · monthly &amp; quarterly · 11 series</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Sort:</span>
          <button
            type="button"
            onClick={() => setSortMode('default')}
            className={sortButtonClass(sortMode === 'default')}
          >
            Importance
          </button>
          <button
            type="button"
            onClick={() => setSortMode('next_release')}
            className={sortButtonClass(sortMode === 'next_release')}
          >
            Next release
          </button>
        </div>
      </div>

      {errors && errors.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-2 text-yellow-200 text-xs">
          {errors.length} indicator{errors.length === 1 ? '' : 's'} failed:{' '}
          {errors.map((e) => e.key).join(', ')}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {sortedIndicators.map((ind) => (
          <div key={ind.key} className="col-span-12 sm:col-span-6 lg:col-span-4">
            <EconomicIndicatorCard indicator={ind} dateRange={dateRange} />
          </div>
        ))}
      </div>

      <RangeControl
        referenceSeries={referenceSeries}
        dataKey="value"
        value={dateRange}
        onChange={setDateRange}
        presets={ECON_PRESETS}
        defaultPreset="5Y"
        title="Economic indicators range"
        caption="Controls all 11 economic indicator cards"
        unitLabel="months"
        referenceLabel="Headline CPI YoY reference · drag the handles or click a preset above"
        yTickFormatter={(v) => `${v.toFixed(1)}%`}
      />
    </div>
  );
}

export default EconomicIndicatorsSection;
